import React, { FC, useEffect, useMemo, useState } from 'react';
import { Drawer, Form } from 'antd';
import moment, { Moment } from 'moment';
import locale from 'antd/es/date-picker/locale/en_GB';
import { Selector } from 'common/components/Selector/Selector';
import CustomDatePicker from 'common/components/DateTime/CustomDatePicker';
import { Calendar, CaretLeft } from 'common/svg';
import {
  CURRENT_WEEK,
  CURRENT_YEAR,
  dayFitsFilters,
  getWeekLabel,
  MONTH_OPTIONS,
  QUARTER_OPTIONS,
  YEAR_OPTIONS,
} from 'common/helpers/date.helper';

moment.locale('en-gb', { week: { dow: 1 } });

interface IFilters {
  year?: number;
  quarter?: number;
  month?: number;
  week?: number;
}

export interface IPeriodFilters extends IFilters {
  date?: string;
}

interface IComponentProps {
  open: boolean;
  year?: number;
  week?: number;
  onYearChange?: (value: number | undefined) => void;
  onWeekChange?: (value: number | undefined) => void;
  // Reports the full filter set (year/quarter/month/week/date) on every change.
  // Pages whose BE supports all period levels (Delivery History) use this
  // instead of onYearChange/onWeekChange.
  onFiltersChange?: (filters: IPeriodFilters) => void;
  onCancel: () => void;
}

export const PeriodFilterCard: FC<IComponentProps> = ({
  open,
  year: yearProp,
  week: weekProp,
  onYearChange,
  onWeekChange,
  onFiltersChange,
  onCancel,
}) => {
  const [openPicker, setOpenPicker] = useState(false);
  const [pickerValue, setPickerValue] = useState<Moment>(moment());
  const [date, setDate] = useState<Moment | null>(null);
  const [filters, setFilters] = useState<IFilters>(() => {
    if (weekProp !== undefined) {
      const effectiveYear = yearProp ?? CURRENT_YEAR;
      const thursday = moment().isoWeekYear(effectiveYear).isoWeek(weekProp).startOf('isoWeek').add(3, 'days');
      return { year: effectiveYear, quarter: thursday.quarter(), month: thursday.month() + 1, week: weekProp };
    }
    return { year: yearProp };
  });

  const { year, quarter, month, week } = filters;

  useEffect(() => {
    if (weekProp === week && yearProp === year) {
      return;
    }

    if (yearProp === undefined && weekProp === undefined) {
      setDate(null);
      setFilters({});
      return;
    }

    if (weekProp !== undefined) {
      const effectiveYear = yearProp ?? CURRENT_YEAR;
      const thursday = moment().isoWeekYear(effectiveYear).isoWeek(weekProp).startOf('isoWeek').add(3, 'days');
      setFilters({
        year: effectiveYear,
        quarter: thursday.quarter(),
        month: thursday.month() + 1,
        week: weekProp,
      });
      setDate(null);
    }
  }, [yearProp, weekProp]);

  const quarterOptions = useMemo(
    () => (!year || year === CURRENT_YEAR ? QUARTER_OPTIONS.slice(0, moment().quarter()) : QUARTER_OPTIONS),
    [year]
  );

  const monthOptions = useMemo(() => {
    const quarterStart = quarter ? (quarter - 1) * 3 + 1 : 1;
    const quarterEnd = quarter ? quarter * 3 : 12;
    const yearMax = !year || year === CURRENT_YEAR ? moment().month() + 1 : 12;
    return MONTH_OPTIONS.filter(({ value }) => value >= quarterStart && value <= Math.min(quarterEnd, yearMax));
  }, [year, quarter]);

  const weekOptions = useMemo(() => {
    const effectiveYear = year ?? CURRENT_YEAR;
    const totalWeeks = moment(`${effectiveYear}-07-01`).isoWeeksInYear();
    const maxWeek = effectiveYear === CURRENT_YEAR ? CURRENT_WEEK : totalWeeks;

    const options = [];
    for (let w = maxWeek; w >= 1; w--) {
      const monday = moment().isoWeekYear(effectiveYear).isoWeek(w).startOf('isoWeek');
      const sunday = monday.clone().add(6, 'days');
      if (
        !dayFitsFilters(monday, effectiveYear, quarter, month) &&
        !dayFitsFilters(sunday, effectiveYear, quarter, month)
      ) {
        continue;
      }
      options.push({ label: getWeekLabel(w, effectiveYear), value: w });
    }
    return options;
  }, [year, quarter, month]);

  const changeOpenPicker = (open: boolean): void => {
    setPickerValue(date ?? moment());
    setOpenPicker(open);
  };

  const handleDateChange = (value: Moment | null): void => {
    setDate(value);
    if (value) {
      const newWeek = value.isoWeekYear() === value.year() ? value.isoWeek() : undefined;
      const newFilters = { year: value.year(), quarter: value.quarter(), month: value.month() + 1, week: newWeek };
      setFilters(newFilters);
      onFiltersChange?.({ ...newFilters, date: value.format('YYYY-MM-DD') });
      onYearChange?.(value.year());
      onWeekChange?.(newWeek);
    } else {
      setFilters({});
      onFiltersChange?.({});
      onYearChange?.(undefined);
      onWeekChange?.(undefined);
    }
  };

  // The date picker value survives a year/quarter/month/week change only if it still fits it;
  // a kept date is re-reported to onFiltersChange (date has top window priority on BE).
  const keptDate = (keep: boolean): string | undefined => {
    if (!keep || !date) {
      setDate(null);
      return undefined;
    }
    return date.format('YYYY-MM-DD');
  };

  const handleYearChange = (value: number | undefined): void => {
    const newFilters = { year: value };
    setFilters(newFilters);
    const dateStr = keptDate(!!value && !!date && date.year() === value);
    onFiltersChange?.({ ...newFilters, date: dateStr });
    onYearChange?.(value);
    onWeekChange?.(undefined);
  };

  const handleQuarterChange = (value: number | undefined): void => {
    const newYear = year ?? (value ? CURRENT_YEAR : undefined);
    const newFilters = { year: newYear, quarter: value };
    setFilters(newFilters);
    const dateStr = keptDate(!!value && !!date && date.quarter() === value);
    onFiltersChange?.({ ...newFilters, date: dateStr });
    if (newYear !== year) {
      onYearChange?.(newYear);
    }
    if (week !== undefined) {
      onWeekChange?.(undefined);
    }
  };

  const handleMonthChange = (value: number | undefined): void => {
    const newYear = year ?? (value ? CURRENT_YEAR : undefined);
    const newFilters = {
      year: newYear,
      quarter: quarter ?? (value ? Math.ceil(value / 3) : undefined),
      month: value,
    };
    setFilters(newFilters);
    const dateStr = keptDate(!!value && !!date && date.month() + 1 === value);
    onFiltersChange?.({ ...newFilters, date: dateStr });
    if (newYear !== year) {
      onYearChange?.(newYear);
    }
    if (week !== undefined) {
      onWeekChange?.(undefined);
    }
  };

  const handleWeekChange = (value: number | undefined): void => {
    let newFilters: IFilters;
    if (value) {
      const effectiveYear = year ?? CURRENT_YEAR;
      const thursday = moment().isoWeekYear(effectiveYear).isoWeek(value).startOf('isoWeek').add(3, 'days');
      newFilters = {
        year: year ?? effectiveYear,
        quarter: quarter ?? thursday.quarter(),
        month: month ?? thursday.month() + 1,
        week: value,
      };
      if (!year) {
        onYearChange?.(effectiveYear);
      }
    } else {
      newFilters = { ...filters, week: undefined };
    }
    setFilters(newFilters);
    const dateStr = keptDate(!!value && !!date && date.isoWeek() === value);
    onFiltersChange?.({ ...newFilters, date: dateStr });
    onWeekChange?.(value);
  };

  return (
    <Drawer
      rootClassName="period-filter-card"
      width={300}
      title="Filter"
      open={open}
      onClose={onCancel}
      getContainer={false}
    >
      <Form className="form" layout="vertical">
        <Form.Item label="Delivery Date">
          <CustomDatePicker
            className={`width-full date-picker ${date ? 'date-picker-with-value' : ''}`}
            popupClassName="date-picker-popup"
            suffixIcon={<Calendar className={date ? 'icon-purple' : 'icon-gray'} />}
            open={openPicker}
            onOpenChange={changeOpenPicker}
            format="MMMM DD, YYYY"
            placement="bottomLeft"
            locale={locale}
            showNow={false}
            superNextIcon={null}
            superPrevIcon={null}
            disabledDate={(current) => current && current > moment().endOf('day')}
            value={date}
            onChange={handleDateChange}
            pickerValue={pickerValue}
            panelRender={(panelNode) => (
              <>
                <div className="date-picker-popup__header">
                  <span className="ff-medium fs-xs">{pickerValue.format('MMMM YYYY')}</span>
                  <div className="flex flex-align-center">
                    <CaretLeft className="mr-5" onClick={() => setPickerValue((p) => p.clone().add(-1, 'month'))} />
                    <CaretLeft
                      style={{ transform: 'rotate(180deg)' }}
                      onClick={() => setPickerValue((p) => p.clone().add(1, 'month'))}
                    />
                  </div>
                </div>
                {panelNode}
              </>
            )}
          />
        </Form.Item>
        <Form.Item label="Year">
          <Selector
            placeholder="Not selected"
            options={YEAR_OPTIONS}
            value={year}
            onChange={(v) => handleYearChange(v as number | undefined)}
          />
        </Form.Item>
        <Form.Item label="Quarter">
          <Selector
            placeholder="Not selected"
            options={quarterOptions}
            value={quarter}
            onChange={(v) => handleQuarterChange(v as number | undefined)}
          />
        </Form.Item>
        <Form.Item label="Month">
          <Selector
            placeholder="Not selected"
            options={monthOptions}
            value={month}
            onChange={(v) => handleMonthChange(v as number | undefined)}
          />
        </Form.Item>
        <Form.Item label="Week">
          <Selector
            placeholder="Not selected"
            options={weekOptions}
            value={week}
            onChange={(v) => handleWeekChange(v as number | undefined)}
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
};
